import type { FormEvent } from 'react'
import type { DateRange } from 'react-day-picker'

import { useQueries } from '@tanstack/react-query'
import { addDays, format, isBefore, isValid, parse, startOfDay } from 'date-fns'
import { X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import type { FileResponseDTO } from '@/utils/api/file-service/models'
import type {
  Department,
  LeaveRequestCreateDto,
  LeaveTypeDto,
  MyEligibleLeaveTypesParams,
} from '@/utils/api/leave-service/models'

import { UserSearchInput } from '@/components/common/UserSearchInput'
import { FilePondUpload } from '@/components/file-pond-upload'
import SearchSelect from '@/components/search-select'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { DateRangePicker } from '@/components/ui/date-range-picker'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useAuth } from '@/features/authentication/components/auth-context'
import { useCurrentYearHolidays } from '@/hooks/use-current-year-holidays'
import { handleError } from '@/lib/utils'
import { getFileMetadata, useDeleteFile } from '@/utils/api/file-service'
import {
  useEligibleLeaveTypesForUser,
  useSubmitLeave,
} from '@/utils/api/leave-service'

export interface LeaveRequestFormState {
  leaveTypeId: string
  startDate: string
  endDate: string
  reason: string
  requestedDays: string
  coveringUserId?: number
}

export interface LeaveRequestFormContext {
  department?: Department
  leaveType?: LeaveTypeDto
  attachmentIds: string[]
  coveringUserId: number
}

export interface LeaveRequestFormProps {
  disableUntilUserSelected?: boolean
  eligibleUserId?: number
  allowPastDates?: boolean
  leaveTypesLoading?: boolean
  myEligibleLeaveTypesData: LeaveTypeDto[]
  initialState?: LeaveRequestFormState
  initialAttachmentIds?: string[]
  departmentIdOverride?: number
  submitLabel?: string
  resetLabel?: string
  showResetButton?: boolean
  onSubmitted?: () => void
  onSubmitOverride?: (
    payload: LeaveRequestCreateDto,
    context: LeaveRequestFormContext,
  ) => void | Promise<void>
}

const DEFAULT_FORM_STATE: LeaveRequestFormState = {
  leaveTypeId: '',
  startDate: '',
  endDate: '',
  reason: '',
  requestedDays: '',
}

export function LeaveRequestForm({
  disableUntilUserSelected = false,
  eligibleUserId,
  allowPastDates = false,
  initialState,
  leaveTypesLoading,
  initialAttachmentIds,
  myEligibleLeaveTypesData,
  submitLabel = 'Submit Request',
  resetLabel = 'Reset Form',
  showResetButton = true,
  onSubmitted,
  onSubmitOverride,
}: LeaveRequestFormProps) {
  const { user: currentUser } = useAuth()
  const [formState, setFormState] = useState<LeaveRequestFormState>(initialState ?? DEFAULT_FORM_STATE)
  const [coveringUserId, setCoveringUserId] = useState<number | undefined>(initialState?.coveringUserId)
  const [attachmentIds, setAttachmentIds] = useState<string[]>(initialAttachmentIds ?? [])
  const [uploadKey, setUploadKey] = useState(0)
  const [isUploadingFile, setIsUploadingFile] = useState(false)
  const [overrideError, setOverrideError] = useState<string | null>(null)
  const [overridePending, setOverridePending] = useState(false)

  const leaveTypesParams = useMemo<MyEligibleLeaveTypesParams>(() => ({
    pageable: { page: 0, size: 200 },
  }), [])

  const shouldUseMyLeaveTypes = !disableUntilUserSelected && eligibleUserId === undefined

  const eligibleLeaveTypesForUserQuery = useEligibleLeaveTypesForUser(
    eligibleUserId ?? 0,
    leaveTypesParams,
    {
      query: { enabled: !shouldUseMyLeaveTypes && eligibleUserId !== undefined },
    },
  )
  const leaveTypesQuery = shouldUseMyLeaveTypes
    ? myEligibleLeaveTypesData
    : eligibleLeaveTypesForUserQuery.data?.data?.content
  const holidaysQuery = useCurrentYearHolidays()
  const leaveTypes = useMemo(() => leaveTypesQuery ?? [], [leaveTypesQuery])
  const holidays = useMemo(() => holidaysQuery.data?.data?.content ?? [], [holidaysQuery.data])
  const submitMutation = useSubmitLeave()
  const deleteFileMutation = useDeleteFile({
    mutation: {
      onError: error => handleError(error, 'Unable to delete file. Please try again.'),
    },
  })
  const [removingFileId, setRemovingFileId] = useState<string | null>(null)

  const selectedLeaveType = useMemo(() => {
    const parsedLeaveTypeId = Number(formState.leaveTypeId)
    if (Number.isNaN(parsedLeaveTypeId)) {
      return undefined
    }
    return leaveTypes.find(item => item.id === parsedLeaveTypeId)
  }, [formState.leaveTypeId, leaveTypes])

  const entitlementRule = selectedLeaveType?.entitlements?.[0]
  const includesWeekendsAndHolidays = entitlementRule?.includeWeekendsAndHolidays
  const requiresAttachment = selectedLeaveType?.requiresAttachment === true
  const isEntitlementDepleted = selectedLeaveType?.entitlementDepleted === true
  const leaveBalance = selectedLeaveType?.balance
  const annualRemaining = leaveBalance?.annualRemaining
  const carryoverRemaining = leaveBalance?.carryoverRemaining
  const carryoverExpiryDate = leaveBalance?.carryoverExpiryDate
  const totalRemaining = leaveBalance?.totalRemaining ?? selectedLeaveType?.remainingDays
  const borrowingAllowed = leaveBalance?.borrowingAllowed
    ?? selectedLeaveType?.carryoverPolicy?.borrowingAllowed
    ?? false
  const maxBorrowDays = leaveBalance?.maxBorrowDays ?? selectedLeaveType?.carryoverPolicy?.maxBorrowDays
  const borrowableRemaining = leaveBalance?.borrowableRemaining ?? maxBorrowDays
  const availableBorrowDays = borrowingAllowed ? Math.max(0, borrowableRemaining ?? 0) : 0
  const requestableDaysLimit = typeof totalRemaining === 'number'
    ? totalRemaining + availableBorrowDays
    : undefined
  const isLeaveUnavailable = requestableDaysLimit !== undefined
    ? requestableDaysLimit <= 0
    : isEntitlementDepleted
  const noticePeriodDays = selectedLeaveType?.noticePeriodDays ?? 0
  const today = startOfDay(new Date())
  const minStartDate = allowPastDates ? undefined : addDays(today, Math.max(noticePeriodDays, 0))
  const calendarStartMonth = minStartDate
    ? new Date(minStartDate.getFullYear(), minStartDate.getMonth(), 1)
    : undefined
  const calendarEndMonth = minStartDate
    ? new Date(minStartDate.getFullYear() + 1, 11)
    : undefined
  const shouldDisableNonWorkingDays = includesWeekendsAndHolidays === false
  const validAttachmentIds = attachmentIds.filter(id => id.trim() !== '')
  const attachmentsSectionVisible = !isLeaveUnavailable
  const hasAttachments = validAttachmentIds.length > 0
  const attachmentMetadataQueries = useQueries({
    queries: validAttachmentIds.map(fileId => ({
      queryKey: ['file-metadata', fileId],
      queryFn: ({ signal }) => getFileMetadata(fileId, { signal }),
      enabled: Boolean(fileId),
    })),
  })
  const reasonColumnClass = attachmentsSectionVisible ? 'md:col-span-2' : 'md:col-span-4'

  const holidayMonthDaySet = useMemo(() => {
    const dates = new Set<string>()
    holidays.forEach((holiday) => {
      if (!holiday.workingDayOverride && holiday.holidayDate) {
        const monthDay = holiday.holidayDate.slice(5, 10)
        if (monthDay.length === 5) {
          dates.add(monthDay)
        }
      }
    })
    return dates
  }, [holidays])

  const selectedRange = useMemo<DateRange | undefined>(() => {
    const trimmedStart = formState.startDate.trim()
    const trimmedEnd = formState.endDate.trim()
    const parsedStart = trimmedStart ? parse(trimmedStart, 'yyyy-MM-dd', new Date()) : undefined
    const parsedEnd = trimmedEnd ? parse(trimmedEnd, 'yyyy-MM-dd', new Date()) : undefined
    const startDate = parsedStart && isValid(parsedStart) ? parsedStart : undefined
    const endDate = parsedEnd && isValid(parsedEnd) ? parsedEnd : undefined
    return startDate ? { from: startDate, to: endDate } : undefined
  }, [formState.endDate, formState.startDate])

  const startDateKey = selectedRange?.from ? format(selectedRange.from, 'MM-dd') : ''
  const endDateKey = selectedRange?.to ? format(selectedRange.to, 'MM-dd') : ''
  const isStartDateAllowed = !selectedRange?.from || !shouldDisableNonWorkingDays || (
    !holidayMonthDaySet.has(startDateKey)
    && selectedRange.from.getDay() !== 0
    && selectedRange.from.getDay() !== 6
  )
  const isEndDateAllowed = !selectedRange?.to || !shouldDisableNonWorkingDays || (
    !holidayMonthDaySet.has(endDateKey)
    && selectedRange.to.getDay() !== 0
    && selectedRange.to.getDay() !== 6
  )
  const hasRequiredAttachment = !requiresAttachment || validAttachmentIds.length > 0
  const isNoticePeriodSatisfied = allowPastDates
    || !selectedRange?.from
    || !minStartDate
    || !isBefore(selectedRange.from, minStartDate)
  const parsedRequestedDays = formState.requestedDays === undefined || formState.requestedDays.trim() === ''
    ? undefined
    : Number(formState.requestedDays)
  const isRequestedDaysOverBalanceLimit = parsedRequestedDays !== undefined
    && !Number.isNaN(parsedRequestedDays)
    && requestableDaysLimit !== undefined
    && parsedRequestedDays > requestableDaysLimit
  const requestUsesBorrowing = parsedRequestedDays !== undefined
    && !Number.isNaN(parsedRequestedDays)
    && borrowingAllowed
    && typeof totalRemaining === 'number'
    && parsedRequestedDays > totalRemaining
    && !isRequestedDaysOverBalanceLimit
  const requestedDaysLabel = parsedRequestedDays === 1 ? 'day' : 'days'
  const requestableDaysLabel = requestableDaysLimit === 1 ? 'day' : 'days'
  const overBalanceMessage = isRequestedDaysOverBalanceLimit
    && parsedRequestedDays !== undefined
    && requestableDaysLimit !== undefined
    ? `Requested ${parsedRequestedDays} ${requestedDaysLabel}, but only ${requestableDaysLimit} ${requestableDaysLabel} are available for this leave type.`
    : 'Requested days exceed the allowed balance for this leave type.'
  const hasSelectedLeaveType = formState.leaveTypeId.trim() !== ''
  const canSelectLeaveType = !leaveTypesLoading && (!disableUntilUserSelected || eligibleUserId !== undefined)
  const canInteractWithForm = hasSelectedLeaveType && canSelectLeaveType
  const applicantUserId = eligibleUserId ?? currentUser?.id
  const isCoveringSameAsApplicant = coveringUserId !== undefined
    && applicantUserId !== undefined
    && coveringUserId === applicantUserId

  const isUploadPending = isUploadingFile && !hasAttachments
  const canSubmit = canInteractWithForm
    && coveringUserId !== undefined
    && !isCoveringSameAsApplicant
    && formState.startDate.trim() !== ''
    && formState.endDate.trim() !== ''
    && isStartDateAllowed
    && isEndDateAllowed
    && hasRequiredAttachment
    && isNoticePeriodSatisfied
    && !isRequestedDaysOverBalanceLimit
    && !isLeaveUnavailable
    && !isUploadPending

  /* eslint-disable react-hooks-extra/no-direct-set-state-in-use-effect */
  const resetFormState = useCallback(
    (nextState: LeaveRequestFormState | undefined, nextAttachmentIds: string[] | undefined) => {
      setFormState(nextState ?? DEFAULT_FORM_STATE)
      setCoveringUserId(nextState?.coveringUserId)
      setAttachmentIds(nextAttachmentIds ?? [])
      setUploadKey(prev => prev + 1)
    },
    [],
  )

  const clearLeaveTypeId = useCallback(() => {
    setFormState(prev => ({ ...prev, leaveTypeId: '' }))
    setCoveringUserId(undefined)
  }, [])
  /* eslint-enable react-hooks-extra/no-direct-set-state-in-use-effect */

  useEffect(() => {
    resetFormState(initialState, initialAttachmentIds)
  }, [initialAttachmentIds, initialState, resetFormState])

  useEffect(() => {
    if (disableUntilUserSelected) {
      if (eligibleUserId === undefined) {
        resetFormState(DEFAULT_FORM_STATE, initialAttachmentIds)
      }
      else {
        clearLeaveTypeId()
      }
    }
  }, [clearLeaveTypeId, disableUntilUserSelected, eligibleUserId, initialAttachmentIds, resetFormState])

  const handleReset = () => {
    resetFormState(initialState, initialAttachmentIds)
    setOverrideError(null)
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!canSubmit || submitMutation.isPending || overridePending || !canInteractWithForm) {
      return
    }

    const parsedLeaveTypeId = Number(formState.leaveTypeId)
    if (Number.isNaN(parsedLeaveTypeId)) {
      return
    }
    if (coveringUserId === undefined) {
      return
    }
    if (isCoveringSameAsApplicant) {
      toast.error('Covering officer cannot be the same as the applicant.')
      return
    }

    const parsedRequestedDays = formState.requestedDays.trim() === ''
      ? undefined
      : Number(formState.requestedDays)

    const payload: LeaveRequestCreateDto = {
      leaveTypeId: parsedLeaveTypeId,
      startDate: formState.startDate.trim(),
      endDate: formState.endDate.trim(),
      reason: formState.reason.trim() || undefined,
      requestedDays: parsedRequestedDays === undefined || Number.isNaN(parsedRequestedDays)
        ? undefined
        : parsedRequestedDays,
      attachmentIds: validAttachmentIds.length > 0 ? validAttachmentIds : undefined,
      coveringUserId,
    }

    if (onSubmitOverride) {
      setOverrideError(null)
      setOverridePending(true)
      try {
        const selectedLeaveType = leaveTypes.find(item => item.id === parsedLeaveTypeId)
        await onSubmitOverride(payload, {
          leaveType: selectedLeaveType,
          attachmentIds: validAttachmentIds,
          coveringUserId,
        })
        handleReset()
      }
      catch (error) {
        setOverrideError(error instanceof Error ? error.message : 'Unable to save changes. Please try again.')
      }
      finally {
        setOverridePending(false)
      }
      return
    }

    try {
      const response = await submitMutation.mutateAsync({ data: payload })

      if (response && response.data.success) {
        handleReset()
        onSubmitted?.()
        toast.success(response.data.message || 'Leave request submitted successfully.')
      }
      else {
        toast.error(response?.data.message || 'Unable to submit leave request.')
      }
    }
    catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to submit leave request.'
      toast.error(message || 'Unable to submit leave request.')
    }
  }

  const handleRemoveAttachment = useCallback(async (fileId: string) => {
    if (!fileId || deleteFileMutation.isPending) {
      return
    }
    setRemovingFileId(fileId)
    try {
      await deleteFileMutation.mutateAsync({ fileId })
      setAttachmentIds(prev => prev.filter(id => id !== fileId))
      toast.success('Attachment removed successfully.')
    }
    catch (error) {
      handleError(error as import('axios').AxiosError, 'Unable to remove attachment. Please try again.')
    }
    finally {
      setRemovingFileId(null)
    }
  }, [deleteFileMutation])

  const handleRequestedDaysChange = useCallback((range: DateRange | undefined) => {
    const start = range?.from
    const end = range?.to
    if (!start || !end) {
      setFormState(prev => ({ ...prev, requestedDays: '' }))
      return
    }

    let count = 0
    let currentDate = start
    while (currentDate <= end) {
      const dateKey = format(currentDate, 'MM-dd')
      if (shouldDisableNonWorkingDays) {
        if (!holidayMonthDaySet.has(dateKey) && currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
          count++
        }
      }
      else {
        count++
      }
      currentDate = addDays(currentDate, 1)
    }
    const value = count.toString()
    setFormState(prev => ({ ...prev, requestedDays: value }))
  }, [holidayMonthDaySet, shouldDisableNonWorkingDays])

  const handleDateSelection = (range: DateRange | undefined) => {
    if (!canInteractWithForm) {
      return
    }
    setFormState(prev => ({
      ...prev,
      startDate: range?.from ? format(range.from, 'yyyy-MM-dd') : '',
      endDate: range?.to ? format(range.to, 'yyyy-MM-dd') : '',
    }))
    handleRequestedDaysChange(range)
  }

  // const disabledDates = useMemo<Date[]>(() => {
  //   const dates: Date[] = []
  //   if (shouldDisableNonWorkingDays) {
  //     holidays.forEach((holiday) => {
  //       if (!holiday.workingDayOverride && holiday.holidayDate) {
  //         const parsedDate = parse(holiday.holidayDate, 'yyyy-MM-dd', new Date())
  //         if (isValid(parsedDate)) {
  //           dates.push(parsedDate)
  //         }
  //       }
  //     })
  //     // Add weekends if selected leave type excludes weekends
  //     const today = new Date()
  //     const lastDateOfYear = lastDayOfYear(today)
  //     let currentDate = today
  //     while (currentDate <= lastDateOfYear) {
  //       if (currentDate.getDay() === 0 || currentDate.getDay() === 6) {
  //         dates.push(currentDate)
  //       }
  //       currentDate = addDays(currentDate, 1)
  //     }

  //   }
  //   return dates
  // }, [holidays, shouldDisableNonWorkingDays])

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      <div className="grid gap-4 md:grid-cols-4">
        <div className="space-y-2 md:col-span-2">
          <SearchSelect
            label="Leave Type"
            placeholder={leaveTypesLoading ? 'Loading leave types...' : 'Select leave type'}
            value={formState.leaveTypeId}
            onValueChange={value => setFormState(prev => ({ ...prev, leaveTypeId: value }))}
            disabled={leaveTypesLoading || (disableUntilUserSelected && eligibleUserId === undefined)}
            options={leaveTypes
              .filter(item => item.id !== undefined)
              .map(item => ({
                value: item.id?.toString() ?? '',
                id: item.id ?? 0,
                name: item.name ?? item.code ?? item.id?.toString() ?? '',
              }))}
          />
        </div>
        {isLeaveUnavailable
          ? (
            <div className="md:col-span-2">
              <Alert variant="destructive">
                <AlertTitle>Leave balance depleted</AlertTitle>
                <AlertDescription>
                  You have no available leave balance for this leave type. Please select another leave type.
                </AlertDescription>
              </Alert>
            </div>
          )
          : null}
        {!isLeaveUnavailable
          ? (
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="leave-date-range">Leave Dates</Label>
              <DateRangePicker
                id="leave-date-range"
                selected={selectedRange}
                onSelect={handleDateSelection}
                numberOfMonths={2}
                displayFormat="MMM do, yyyy"
                defaultMonth={selectedRange?.from ?? minStartDate ?? today}
                disabled={!canInteractWithForm}
                disabledDates={(date) => {
                  const dateKey = format(date, 'MM-dd')
                  if (minStartDate && isBefore(date, minStartDate)) {
                    return true
                  }
                  if (shouldDisableNonWorkingDays) {
                    if (holidayMonthDaySet.has(dateKey)) {
                      return true
                    }
                    if (date.getDay() === 0 || date.getDay() === 6) {
                      return true
                    }
                  }
                  return false
                }}
                captionLayout="dropdown"
                startMonth={calendarStartMonth}
                endMonth={calendarEndMonth}
                autoFocus
              />
            </div>
          )
          : null}

        {!isLeaveUnavailable
          && (
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="requested-days">Requested Days</Label>
              <Input
                id="requested-days"
                type="number"
                min={0}
                disabled={true}
                value={formState.requestedDays}
                placeholder="0"
              />
            </div>
          )}
        {!isLeaveUnavailable
          ? (
            <div className="space-y-2 md:col-span-2">
              <UserSearchInput
                id="covering-user"
                label="Covering Officer"
                placeholder="Search for staff who will fill in..."
                required
                value={coveringUserId}
                prefilledUser={coveringUserId !== undefined ? { id: coveringUserId } : undefined}
                onValueChange={setCoveringUserId}
                disabled={!canInteractWithForm}
              />
            </div>
          )
          : null}
        {!isLeaveUnavailable
          ? (
            <div className={`space-y-2 ${reasonColumnClass}`}>
              <Label htmlFor="leave-reason">Reason</Label>
              <Textarea
                maxLength={255}
                id="leave-reason"
                value={formState.reason}
                aria-errormessage="reason-helptext"
                onChange={event => setFormState(prev => ({ ...prev, reason: event.target.value }))}
                disabled={!canInteractWithForm}
                placeholder="Optional reason for your leave"
              />
              <span className="text-sm text-muted-foreground float-right" id="reason-helptext">
                {formState.reason.length}
                /255 characters
              </span>
            </div>
          )
          : null}
        {attachmentsSectionVisible
          ? (
            <div className="space-y-2 md:col-span-2">
              <Label>{requiresAttachment ? 'Attachments (required)' : 'Attachments'}</Label>
              {hasAttachments
                ? (
                  <div className="space-y-2">
                    {validAttachmentIds.map((fileId, index) => {
                      const query = attachmentMetadataQueries[index]
                      const metadata = query?.data?.data as FileResponseDTO | undefined
                      return (
                        <div
                          key={fileId}
                          className="flex items-center justify-between gap-3 rounded-lg border border-border/70 bg-muted/50 px-3 py-2"
                        >
                          <div className="flex flex-col text-sm">
                            <p className="text-sm font-medium text-foreground">
                              {metadata?.originalFilename ?? fileId}
                            </p>
                            {metadata?.path
                              ? (
                                <a
                                  href={metadata.path}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-xs font-medium text-primary underline-offset-2 hover:underline"
                                >
                                  View file
                                </a>
                              )
                              : null}
                            {query?.isFetching
                              ? (
                                <span className="text-xs text-muted-foreground">
                                  Loading metadata…
                                </span>
                              )
                              : null}
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:bg-destructive/10"
                            onClick={() => handleRemoveAttachment(fileId)}
                            disabled={removingFileId === fileId || !canInteractWithForm}
                          >
                            <X className="size-4" />
                            <span className="sr-only">Remove attachment</span>
                          </Button>
                        </div>
                      )
                    })}
                  </div>
                )
                : (
                  <FilePondUpload
                    key={uploadKey}
                    onUploadComplete={(fileId) => {
                      if (!fileId.trim()) {
                        toast.error('Upload completed without a file reference. Please try again.')
                        return
                      }
                      setAttachmentIds(prev => (prev.includes(fileId) ? prev : [...prev, fileId]))
                    }}
                    onRemove={(fileId) => {
                      setAttachmentIds(prev => prev.filter(id => id !== fileId))
                    }}
                    onUploadStateChange={setIsUploadingFile}
                    disabled={!canInteractWithForm}
                  />
                )}
            </div>
          )
          : null}
      </div>

      {holidaysQuery.isError
        ? (
          <Alert variant="destructive">
            <AlertTitle>Unable to load holidays</AlertTitle>
            <AlertDescription>{holidaysQuery.error?.message ?? 'Please refresh the page.'}</AlertDescription>
          </Alert>
        )
        : null}
      {overrideError
        ? (
          <Alert variant="destructive">
            <AlertTitle>Unable to save changes</AlertTitle>
            <AlertDescription>{overrideError}</AlertDescription>
          </Alert>
        )
        : null}
      {isCoveringSameAsApplicant
        ? (
          <Alert variant="destructive">
            <AlertTitle>Invalid covering officer</AlertTitle>
            <AlertDescription>
              Covering officer must be different from the applicant.
            </AlertDescription>
          </Alert>
        )
        : null}
      {requestUsesBorrowing
        ? (
          <Alert>
            <AlertTitle>This request will use borrowed leave</AlertTitle>
            <AlertDescription>
              Approving this request will consume leave from next year entitlement.
            </AlertDescription>
          </Alert>
        )
        : null}
      {isRequestedDaysOverBalanceLimit
        ? (
          <Alert variant="destructive">
            <AlertTitle>Requested days exceed available balance</AlertTitle>
            <AlertDescription>{overBalanceMessage}</AlertDescription>
          </Alert>
        )
        : null}
      {requiresAttachment && validAttachmentIds.length === 0 && !isUploadingFile
        ? (
          <Alert variant="default">
            <AlertTitle>Attachment Required</AlertTitle>
            <AlertDescription>
              This leave type requires at least one attachment. Please upload a document before submitting.
            </AlertDescription>
          </Alert>
        )
        : null}
      {!isLeaveUnavailable && selectedLeaveType
        ? (
          <div className="rounded-lg border border-border/70 bg-muted/30 p-3">
            <p className="text-sm font-medium">Balance snapshot</p>
            <div className="mt-2 grid gap-2 text-sm sm:grid-cols-2">
              <p>
                Annual remaining:
                {' '}
                <span className="font-medium">
                  {annualRemaining ?? '—'}
                  {' '}
                  days
                </span>
              </p>
              <p>
                Total remaining:
                {' '}
                <span className="font-medium">
                  {totalRemaining ?? '—'}
                  {' '}
                  days
                </span>
              </p>
              <p>
                Carryover remaining:
                {' '}
                <span className="font-medium">
                  {carryoverRemaining ?? 0}
                  {' '}
                  days
                </span>
              </p>
              <p>
                Carryover expiry:
                {' '}
                <span className="font-medium">
                  {carryoverExpiryDate
                    ? new Date(carryoverExpiryDate).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'short',
                      day: '2-digit',
                    })
                    : '—'}
                </span>
              </p>
              <p>
                Borrowing:
                {' '}
                <span className="font-medium">{borrowingAllowed ? 'Allowed' : 'Not allowed'}</span>
              </p>
              {borrowingAllowed
                ? (
                  <p>
                    Borrowable remaining:
                    {' '}
                    <span className="font-medium">
                      {availableBorrowDays}
                      {' '}
                      days
                    </span>
                  </p>
                )
                : null}
            </div>
          </div>
        )
        : null}

      <div className="flex flex-wrap items-center gap-3 justify-end">
        {!canSubmit && canInteractWithForm
          ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="inline-flex">
                  <Button type="button" disabled>
                    {submitLabel}
                  </Button>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {isUploadingFile && !hasAttachments
                  ? 'Please wait for file upload to complete'
                  : requiresAttachment && validAttachmentIds.length === 0
                    ? 'Please upload required attachment'
                    : coveringUserId === undefined
                      ? 'Please select a covering officer'
                      : isCoveringSameAsApplicant
                        ? 'Covering officer must be different from applicant'
                        : formState.startDate.trim() === '' || formState.endDate.trim() === ''
                          ? 'Please select leave dates'
                          : !isStartDateAllowed || !isEndDateAllowed
                            ? 'Selected dates include non-working days'
                            : !isNoticePeriodSatisfied
                              ? `Leave must start at least ${noticePeriodDays} days from today`
                              : isRequestedDaysOverBalanceLimit
                                ? overBalanceMessage
                                : 'Please complete all required fields'}
              </TooltipContent>
            </Tooltip>
          )
          : (
            <Button type="submit" disabled={submitMutation.isPending || overridePending}>
              {submitMutation.isPending || overridePending ? 'Submitting...' : submitLabel}
            </Button>
          )}
        {showResetButton
          ? (
            <Button type="button" variant="outline" onClick={handleReset}>
              {resetLabel}
            </Button>
          )
          : null}
      </div>
    </form>
  )
}
